import React, { useEffect, useState } from 'react';
import { X, Clock, Activity, Calendar } from 'lucide-react';
import { db, GoalTemplate, TaskTemplate, TaskCompletionStatistics } from '../../lib/db';

interface TemplateDetailsModalProps {
  template: GoalTemplate;
  userId: string;
  onClose: () => void;
}

interface TaskWithStats extends TaskTemplate {
  statistics?: TaskCompletionStatistics;
}

export function TemplateDetailsModal({ template, userId, onClose }: TemplateDetailsModalProps) {
  const [tasks, setTasks] = useState<TaskWithStats[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadTemplateDetails();
  }, [template.id]);

  const loadTemplateDetails = async () => {
    try {
      setLoading(true);
      
      // Load task templates for this goal template
      const taskTemplates = await db.taskTemplates.findByGoalTemplateId(template.id);
      
      // Get statistics for each task
      const tasksWithStats = await Promise.all(
        taskTemplates.map(async (task) => {
          const stats = await db.taskCompletionStatistics.getEstimate(
            task.title,
            task.description,
            userId,
            template.id
          );
          return { ...task, statistics: stats || undefined };
        })
      );
      
      setTasks(tasksWithStats);
    } catch (error) {
      console.error('Error loading template details:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDuration = (minutes: number) => {
    if (minutes < 60) {
      return `${Math.round(minutes)} min`;
    }
    const hours = Math.floor(minutes / 60);
    const mins = Math.round(minutes % 60);
    if (mins === 0) {
      return `${hours} hr`;
    }
    return `${hours} hr ${mins} min`;
  };

  const totalEstimatedTime = tasks.reduce((sum, task) => 
    sum + (task.statistics?.avg_minutes_per_completion || 0), 0
  );

  const tasksWithStats = tasks.filter(t => t.statistics);
  const averageCompletionRate = tasksWithStats.length > 0
    ? tasksWithStats.reduce((sum, task) => 
        sum + (task.statistics?.total_completions || 0), 0
      ) / tasksWithStats.length
    : 0;

  return (
    <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full mx-4 max-h-[90vh] overflow-hidden flex flex-col">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">{template.title}</h2>
            {template.description && (
              <p className="text-sm text-gray-600 mt-1">{template.description}</p>
            )}
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-500 transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto">
          {/* Summary Stats */}
          <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
            <div className="grid grid-cols-3 gap-4">
              <div className="text-center">
                <div className="flex items-center justify-center mb-2">
                  <Activity className="w-5 h-5 text-blue-500" />
                </div>
                <div className="text-2xl font-semibold text-gray-900">{tasks.length}</div>
                <div className="text-sm text-gray-600">Total Tasks</div>
              </div>
              <div className="text-center">
                <div className="flex items-center justify-center mb-2">
                  <Clock className="w-5 h-5 text-green-500" />
                </div>
                <div className="text-2xl font-semibold text-gray-900">
                  {totalEstimatedTime > 0 ? formatDuration(totalEstimatedTime) : 'N/A'}
                </div>
                <div className="text-sm text-gray-600">Est. Total Time</div>
              </div>
              <div className="text-center">
                <div className="flex items-center justify-center mb-2">
                  <Calendar className="w-5 h-5 text-purple-500" />
                </div>
                <div className="text-2xl font-semibold text-gray-900">
                  {averageCompletionRate > 0 ? Math.round(averageCompletionRate) : 'N/A'}
                </div>
                <div className="text-sm text-gray-600">Avg. Completions</div>
              </div>
            </div>
          </div>

          {/* Task List */}
          <div className="px-6 py-4">
            <h3 className="text-sm font-medium text-gray-900 mb-4">Tasks in This Template</h3>
            {loading ? (
              <div className="text-center py-4 text-gray-500">Loading task details...</div>
            ) : tasks.length === 0 ? (
              <div className="text-center py-4 text-gray-500">No tasks in this template</div>
            ) : (
              <div className="space-y-3">
                {tasks.map((task, index) => (
                  <div key={task.id} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-gray-500">#{index + 1}</span>
                          <h4 className="font-medium text-gray-900">{task.title}</h4>
                        </div>
                        {task.description && (
                          <p className="text-sm text-gray-600 mt-1">{task.description}</p>
                        )}
                      </div>
                      <div className="text-right ml-4">
                        {task.statistics ? (
                          <div>
                            <div className="text-sm font-medium text-gray-900">
                              ~{formatDuration(task.statistics.avg_minutes_per_completion)}
                            </div>
                            <div className="text-xs text-gray-500">
                              {task.statistics.total_completions} completions
                            </div>
                            {task.statistics.min_minutes && task.statistics.max_minutes && (
                              <div className="text-xs text-gray-400 mt-1">
                                {formatDuration(task.statistics.min_minutes)} - {formatDuration(task.statistics.max_minutes)}
                              </div>
                            )}
                          </div>
                        ) : (
                          <div className="text-sm text-gray-400">No data yet</div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
          <button
            onClick={onClose}
            className="w-full px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}